import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useIr } from "../shell/navegar.js";
import { Check, CircleAlert, UserPlus } from "lucide-react";
import { Pagina, Cartao, Carregando, Falha } from "../shell/Pagina.js";
import { EmptyState } from "@pessoas/ui";
import { trpc } from "../lib/trpc.js";
import { cpf as fCpf, moeda } from "../lib/formato.js";
import { CATEGORIA_POR_TIPO } from "../lib/br.js";

const hoje = () => new Date().toISOString().slice(0, 10);

/**
 * Admissão.
 *
 * A tela grava TRÊS coisas no mesmo commit: o vínculo, a MOVIMENTAÇÃO (o fato
 * datado) e o `vinculo_estado` (a projeção que responde "qual era o cargo em
 * 15/03"). O banco recusa estado sem movimentação por trás, então não existe
 * caminho em que alguém apareça com um cargo sem o lançamento que o colocou lá.
 */
export function Admissao() {
  const ir = useIr();
  const qc = useQueryClient();
  const [pessoaId, setPessoaId] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const locais = useQuery({
    queryKey: ["cadastro", "estabelecimentos"],
    queryFn: () => trpc.cadastro.estabelecimentos.query(),
  });
  const pessoas = useQuery({
    queryKey: ["cadastro", "pessoas", "", false],
    queryFn: () => trpc.cadastro.pessoas.query({ busca: "", incluirDesligados: false, limite: 500 }),
  });

  // Só quem ainda não tem vínculo aberto pode ser admitido nesta tela.
  const semVinculo = (pessoas.data ?? []).filter((p) => !p.vinculoId);
  const local = locais.data?.[0];

  const [f, setF] = useState({
    estabelecimentoId: "", matricula: "",
    tipo: "clt", categoriaESocial: 101,
    dataAdmissao: hoje(), tipoContrato: "experiencia",
    dataTerminoPrevisto: "", cargo: "", cbo: "",
    salarioBase: "", unidadeSalario: "mensal", cargaHorariaSemanalMin: 2640,
  });

  useEffect(() => {
    if (local && !f.estabelecimentoId) setF((v) => ({ ...v, estabelecimentoId: local.id }));
  }, [local, f.estabelecimentoId]);

  const matricula = useQuery({
    queryKey: ["cadastro", "matricula", local?.empresaId],
    queryFn: () => trpc.cadastro.proximaMatricula.query({ empresaId: local!.empresaId }),
    enabled: !!local?.empresaId,
  });
  useEffect(() => {
    if (matricula.data && !f.matricula) setF((v) => ({ ...v, matricula: matricula.data }));
  }, [matricula.data, f.matricula]);

  const admitir = useMutation({
    mutationFn: () => trpc.cadastro.admitir.mutate({
      pessoaId: pessoaId!, empresaId: local!.empresaId,
      estabelecimentoId: f.estabelecimentoId, matricula: f.matricula,
      tipo: f.tipo as "clt", categoriaESocial: f.categoriaESocial,
      dataAdmissao: f.dataAdmissao,
      tipoContrato: f.tipoContrato as "experiencia",
      dataTerminoPrevisto: f.tipoContrato === "indeterminado" ? null : f.dataTerminoPrevisto,
      cargo: f.cargo.trim(),
      cbo: f.cbo.replace(/\D/g, "") || null,
      salarioBase: f.salarioBase.replace(",", "."),
      unidadeSalario: f.unidadeSalario as "mensal",
      cargaHorariaSemanalMin: f.cargaHorariaSemanalMin,
    }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["cadastro"] });
      void qc.invalidateQueries({ queryKey: ["painel"] });
      ir("/pessoas");
    },
    onError: (e: unknown) => setErro(e instanceof Error ? e.message : String(e)),
  });

  if (locais.isPending || pessoas.isPending) {
    return <Pagina secao="Pessoas" titulo="Admissão"><Carregando linhas={5} /></Pagina>;
  }
  if (!local) {
    return (
      <Pagina secao="Pessoas" titulo="Admissão">
        <EmptyState
          titulo="Sem estabelecimento, ninguém pode ser admitido"
          descricao="O vínculo exige um estabelecimento, e trocar depois significa refazer o S-2200 de todo mundo."
          acao={<button className="botao" data-variante="primario"
                        onClick={() => ir("/configuracoes")}>
                  Cadastrar o estabelecimento
                </button>}
        />
      </Pagina>
    );
  }
  if (semVinculo.length === 0) {
    return (
      <Pagina secao="Pessoas" titulo="Admissão">
        <EmptyState
          titulo="Todo mundo que está cadastrado já tem vínculo aberto"
          descricao="Cadastre a pessoa primeiro. Readmitir alguém como pessoa nova apaga o histórico de exame, o tempo de casa e os dependentes."
          acao={<button className="botao" data-variante="primario"
                        onClick={() => ir("/pessoas")}>
                  Ir para o quadro de pessoas
                </button>}
        />
      </Pagina>
    );
  }

  const txt = (k: keyof typeof f) => ({
    value: String(f[k]),
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setF((v) => ({ ...v, [k]: e.target.value })),
  });

  const pronto = pessoaId && f.estabelecimentoId && f.matricula && f.cargo.trim()
    && f.salarioBase && (f.tipoContrato === "indeterminado" || f.dataTerminoPrevisto);

  return (
    <Pagina
      secao="Pessoas" titulo="Admissão"
      descricao="Cargo e salário entram com vigência, e a movimentação que os criou fica gravada. É daqui que folha, ponto e eSocial passam a ter o que calcular."
    >
      {locais.isError && <Falha erro={locais.error} tentarDeNovo={() => void locais.refetch()} />}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,20rem)_1fr]">
        <Cartao titulo="Quem" subtitulo={`${semVinculo.length} sem vínculo aberto`}>
          <ul className="eop-escolha">
            {semVinculo.map((p) => (
              <li key={p.id}>
                <button type="button" className="eop-escolha-item"
                        data-ativo={p.id === pessoaId}
                        onClick={() => setPessoaId(p.id)}>
                  <span className="min-w-0 flex-1">
                    <span className="eop-escolha-nome">{p.nome}</span>
                    <span className="num eop-escolha-sub">{fCpf(p.cpf)}</span>
                  </span>
                  {p.id === pessoaId && <Check aria-hidden size={14} className="eop-escolha-marca" />}
                </button>
              </li>
            ))}
          </ul>
        </Cartao>

        <Cartao titulo="Contrato" subtitulo="Cargo e salário passam a valer da data de admissão em diante">
          <form className="space-y-3.5"
                onSubmit={(e) => { e.preventDefault(); setErro(null); admitir.mutate(); }}>
            <div className="grid gap-3.5 sm:grid-cols-2">
              <label className="block">
                <span className="eop-rotulo">Estabelecimento</span>
                <select className="campo mt-1.5" {...txt("estabelecimentoId")}>
                  {(locais.data ?? []).map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.matriz ? "Matriz" : "Filial"} {l.uf} · {l.numeroInscricao.slice(-6)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="eop-rotulo">Matrícula</span>
                <input className="campo mt-1.5 num" required {...txt("matricula")} />
              </label>

              <label className="block">
                <span className="eop-rotulo">Tipo de vínculo</span>
                <select className="campo mt-1.5" value={f.tipo}
                        onChange={(e) => {
                          const t = e.target.value;
                          // A categoria do eSocial depende do tipo. Trocar um
                          // sem o outro manda 101 para um estagiário, que é
                          // rejeição no S-2200.
                          setF((v) => ({ ...v, tipo: t,
                            categoriaESocial: CATEGORIA_POR_TIPO[t]?.[0]?.codigo ?? 101 }));
                        }}>
                  <option value="clt">CLT</option>
                  <option value="aprendiz">Aprendiz</option>
                  <option value="estagio">Estágio</option>
                  <option value="intermitente">Intermitente</option>
                  <option value="diretor">Diretor</option>
                  <option value="autonomo">Autônomo</option>
                  <option value="pj">PJ</option>
                  <option value="terceiro">Terceiro</option>
                </select>
              </label>
              <label className="block">
                <span className="eop-rotulo">Categoria do eSocial</span>
                <select className="campo mt-1.5" value={String(f.categoriaESocial)}
                        onChange={(e) => setF((v) => ({ ...v, categoriaESocial: Number(e.target.value) }))}>
                  {(CATEGORIA_POR_TIPO[f.tipo] ?? []).map((c) => (
                    <option key={c.codigo} value={c.codigo}>{c.nome}</option>
                  ))}
                </select>
              </label>

              <label className="block">
                <span className="eop-rotulo">Data de admissão</span>
                <input className="campo mt-1.5" type="date" required {...txt("dataAdmissao")} />
              </label>
              <label className="block">
                <span className="eop-rotulo">Tipo de contrato</span>
                <select className="campo mt-1.5" {...txt("tipoContrato")}>
                  <option value="experiencia">Experiência</option>
                  <option value="indeterminado">Prazo indeterminado</option>
                  <option value="determinado">Prazo determinado</option>
                </select>
              </label>

              {f.tipoContrato !== "indeterminado" && (
                <label className="block sm:col-span-2">
                  <span className="eop-rotulo">Término previsto</span>
                  <input className="campo mt-1.5" type="date" required {...txt("dataTerminoPrevisto")} />
                  <span className="carimbo mt-1 block">
                    {/* Art. 445, parágrafo único: experiência tem no máximo 90
                        dias e UMA prorrogação. Vencer sem decisão transforma em
                        prazo indeterminado no silêncio. */}
                    experiência vai a no máximo 90 dias, com uma única prorrogação
                  </span>
                </label>
              )}

              <label className="block">
                <span className="eop-rotulo">Cargo</span>
                <input className="campo mt-1.5" required {...txt("cargo")} />
              </label>
              <label className="block">
                <span className="eop-rotulo">CBO</span>
                <input className="campo mt-1.5 num" inputMode="numeric" placeholder="352210"
                       {...txt("cbo")} />
              </label>

              <label className="block">
                <span className="eop-rotulo">Salário base</span>
                <input className="campo mt-1.5 num" required inputMode="decimal"
                       placeholder="2500.00" {...txt("salarioBase")} />
                {f.salarioBase && (
                  <span className="carimbo mt-1 block">
                    {moeda(f.salarioBase.replace(",", "."))} por {f.unidadeSalario}
                  </span>
                )}
              </label>
              <label className="block">
                <span className="eop-rotulo">Unidade</span>
                <select className="campo mt-1.5" {...txt("unidadeSalario")}>
                  <option value="mensal">Mensal</option>
                  <option value="quinzenal">Quinzenal</option>
                  <option value="semanal">Semanal</option>
                  <option value="diario">Diário</option>
                  <option value="horario">Por hora</option>
                  <option value="tarefa">Por tarefa</option>
                </select>
              </label>

              <label className="block sm:col-span-2">
                <span className="eop-rotulo">Carga horária semanal</span>
                <select className="campo mt-1.5" value={String(f.cargaHorariaSemanalMin)}
                        onChange={(e) => setF((v) => ({ ...v, cargaHorariaSemanalMin: Number(e.target.value) }))}>
                  {/* Em MINUTOS inteiros, nunca em horas com decimal: 8,5h
                      contra 510min é a diferença entre acumular erro de ponto
                      flutuante ao somar 220 dias e não acumular. */}
                  <option value="2640">44 horas</option>
                  <option value="2400">40 horas</option>
                  <option value="1800">30 horas</option>
                  <option value="1200">20 horas</option>
                  <option value="1080">18 horas</option>
                </select>
              </label>
            </div>

            {erro && (
              <p className="eop-erro" role="alert">
                <CircleAlert aria-hidden size={15} /><span>{erro}</span>
              </p>
            )}

            <button type="submit" className="botao w-full" data-variante="primario"
                    disabled={!pronto || admitir.isPending}>
              <UserPlus aria-hidden size={15} />
              {admitir.isPending ? "Admitindo" : "Admitir"}
            </button>
            {!pessoaId && (
              <p className="carimbo text-center">escolha a pessoa na lista ao lado</p>
            )}
          </form>
        </Cartao>
      </div>
    </Pagina>
  );
}
